/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import type { IPRAttributes, TargetIndustry, EnergySource } from '.';
import {
  CertificateExpression,
  ForDisabledPeopleExpression,
  ForIndustryExpression,
  ForSpecialEndUseExpression,
  LogicalMixinExpression,
  ExpressionType,
  PureProductExpression,
  RecyclabilityExpression,
} from './types';

/**
 * Static mappings from industry names to product codes.
 * These mappings are used when converting ForIndustry expressions.
 * Keys are TargetIndustry enum values (strings).
 */
export const INDUSTRY_TO_PRODUCT_CODE: Partial<Record<TargetIndustry, string>> = {
  'Agricultural industry': 'AGRIN',
  'Aviation industry': 'AVIIN',
  'Beauty industry': 'BEAIN',
  'Chemicals industry': 'CHEIN',
  'Clothing industry': 'CLOIB',
  'Education industry': 'EDUIA',
  'Entertainment industry': 'ENTIN',
  'Telecommunications industry': 'TELIA',
  'Electronics industry': 'ELEIA',
  'Restaurant industry': 'RESIA',
  'Financial services industry': 'FISEB',
  'Food and beverage industry': 'FOBEI',
  'Fossil fuels industry': 'FOFUI',
  'Healthcare industry': 'HEAIN',
  'Maritime industry': 'MARIB',
  'Packaging industry': 'PACIN',
  'Plastics industry': 'PLAIB',
  'Renewable energy industry': 'REENI',
  'Retail industry': 'RETIN',
  'Travel industry': 'TRAIB',
  'Mining industry': 'MINIC',
  'Pharmaceutical industry': 'PHAIA',
  'Transportation and logistics industry': 'TRLOI',
  'Cargo shipping industry': 'CASHI',
  'Waste management and recycling industry': 'WAMAS',
  'Forestry, pulp and paper industry': 'PUPAI',
  'Cement and concrete industry': 'CEMEN',
  'Nuclear energy industry': 'NUCIN',
  'Bioenergy industry': 'BIOIN',
  'Space industry': 'SPACE',
  'Alcoholic beverages industry': 'ALCBE',
  'Security and defence industry': 'DEFIN',
  'Social media industry': 'SOCME',
  'Digital infrastructure and AI industry': undefined, // No direct product code match
  'Content publishing industry': 'CSDRS',
  'Fishing and aquaculture industry': 'FASAB',
  'Meat and dairy industry': undefined, // No direct product code match
  'Human resources industry': 'HURES',
  'Metals industry': 'STEIN', // Using Steel industry as closest match
  'Real estate and construction industry': 'REEUG',
};

/**
 * Static mappings from energy source enum to product codes.
 * These mappings are used when converting UsingEnergySource expressions.
 * Keys are EnergySource enum values (strings).
 */
export const ENERGY_SOURCE_TO_PRODUCT_CODE: Record<EnergySource, string | undefined> = {
  'Fossil Fuels': 'FOSSI',
  Nuclear: 'NUCPO',
  Renewables: 'RENEA',
  Bioenergy: 'BIOEN',
};

function iprIndustryToPureProduct(industry: TargetIndustry): PureProductExpression {
  const productCode = INDUSTRY_TO_PRODUCT_CODE[industry];
  return {
    type: ExpressionType.PureProduct,
    productCode,
    title: industry,
  };
}

// function iprEnergySourceToPureProduct(energySource: EnergySource): PureProductExpression {
//   const productCode = ENERGY_SOURCE_TO_PRODUCT_CODE[energySource];
//   return {
//     type: ExpressionType.PureProduct,
//     productCode,
//     title: energySource,
//   };
// }

function wrapInCombinationIfMultiple(operands: LogicalMixinExpression[]): LogicalMixinExpression {
  return operands.length === 1
    ? operands[0]
    : {
        type: ExpressionType.Combination,
        combinations: operands,
      };
}

function wrapInIndustry(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
  if (!ipr.TargetIndustry || ipr.TargetIndustry.length === 0) {
    return currentProduct;
  }
  const industries = ipr.TargetIndustry.map(industry => {
    return iprIndustryToPureProduct(industry);
  });
  const ret: ForIndustryExpression = {
    type: ExpressionType.ForIndustry,
    baseProduct: currentProduct,
    forProduct: wrapInCombinationIfMultiple(industries),
  };
  return ret;
}

function wrapInCertificates(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
  if (!ipr.Certifications || ipr.Certifications.length === 0) {
    return currentProduct;
  }
  let ret = currentProduct;
  for (const cert of ipr.Certifications) {
    const co: CertificateExpression = {
      type: ExpressionType.Certificate,
      certificate: cert,
      baseProduct: ret,
    };
    ret = co;
  }
  return ret;
}

function wrapInRecyclability(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
  if (!ipr.Recyclability || ipr.Recyclability.length === 0) {
    return currentProduct;
  }
  let ret = currentProduct;
  for (const recyclability of ipr.Recyclability) {
    const ro: RecyclabilityExpression = {
      type: ExpressionType.Recyclability,
      recyclability: recyclability,
      baseProduct: ret,
    };
    ret = ro;
  }
  return ret;
}

// function wrapInEnergySource(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
//   if (!ipr.EnergySourcesUsed || ipr.EnergySourcesUsed.length === 0) {
//     return currentProduct;
//   }
//   const energySources = ipr.EnergySourcesUsed.map(energySource => {
//     return iprEnergySourceToPureProduct(energySource);
//   });
//   const ret: UsingEnergySourceExpression = {
//     type: ExpressionType.UsingEnergySource,
//     baseProduct: currentProduct,
//     usingEnergySource: wrapInCombinationIfMultiple(energySources),
//   };
//   return ret;
// }

function wrapInSpecialEndUse(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
  const specialEndUses: PureProductExpression[] = [];

  if (ipr.Educational) {
    specialEndUses.push({
      type: ExpressionType.PureProduct,
      productCode: 'EDUCA',
      title: 'Educational',
    });
  }

  if (specialEndUses.length === 0) {
    return currentProduct;
  }

  const ret: ForSpecialEndUseExpression = {
    type: ExpressionType.ForSpecialEndUse,
    baseProduct: currentProduct,
    forSpecialEndUse: wrapInCombinationIfMultiple(specialEndUses),
  };

  return ret;
}

function wrapInForDisabledPeople(currentProduct: LogicalMixinExpression, ipr: Partial<IPRAttributes>) {
  if (!ipr.ForDisabledPeople) {
    return currentProduct;
  }
  const ret: ForDisabledPeopleExpression = {
    type: ExpressionType.ForDisabledPeople,
    baseProduct: currentProduct,
  };
  return ret;
}

export function iprToLogicalMixinExpression(
  ipr: Partial<IPRAttributes>,
  baseProductTitle: string,
  baseProductCode: string,
): LogicalMixinExpression {
  let currentProduct: LogicalMixinExpression = {
    type: ExpressionType.PureProduct,
    title: baseProductTitle,
    productCode: baseProductCode,
  };
  currentProduct = wrapInCertificates(currentProduct, ipr);
  currentProduct = wrapInRecyclability(currentProduct, ipr);
  // currentProduct = wrapInEnergySource(currentProduct, ipr);
  currentProduct = wrapInSpecialEndUse(currentProduct, ipr);
  currentProduct = wrapInForDisabledPeople(currentProduct, ipr);
  currentProduct = wrapInIndustry(currentProduct, ipr);
  return currentProduct;
}

export interface GracefulExpressionResult {
  gracefulExpression: LogicalMixinExpression;
  unmappedTitles: string[];
  isFallBack: boolean;
}
